import { useQuery } from "@tanstack/react-query";

export type PlanOfCareAvailabilityDebugProvider = {
  providerId: string;
  providerName: string;
  reason: string;
};

export type PlanOfCareAvailabilityDebugSlot = {
  providerId: string;
  providerName: string;
  startISO: string;
  endISO: string;
  reason: string;
};

export type PlanOfCareAvailabilityDebug = {
  providers: PlanOfCareAvailabilityDebugProvider[];
  slots: PlanOfCareAvailabilityDebugSlot[];
};

export type PlanOfCareAvailability = {
  availability: {
    [providerId: string]: {
      [date: string]: {
        startISO: string;
        endISO: string;
      }[];
    };
  };
  availableProviders: {
    providerId: string;
    providerName: string;
  }[];
  debug?: PlanOfCareAvailabilityDebug;
};

type PlanOfCareVisitAvailabilityParams = {
  uniqueId?: string;
  enabled: boolean;
  startDate: string;
  endDate: string;
  appointmentTypeId: string;
  planOfCareId?: string;
  // when set, requests extra debug info from the backend and authorizes the
  // request as the logged-in clinic user (rather than the patient link)
  debug?: boolean;
  token?: string;
};

export function usePlanOfCareVisitAvailability({
  uniqueId,
  enabled = true,
  startDate, // 2025-08-25 format
  endDate, // 2025-08-25 format
  appointmentTypeId,
  planOfCareId,
  debug,
  token
}: PlanOfCareVisitAvailabilityParams) {
  return useQuery({
    queryKey: [
      "plan-of-care-visit-availability",
      uniqueId,
      endDate,
      appointmentTypeId,
      planOfCareId,
      debug
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        uniqueId: uniqueId || "",
        startDate,
        endDate,
        appointmentTypeId
      });
      if (planOfCareId) params.set("planOfCareId", planOfCareId);
      if (debug) params.set("debug", "true");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/plan-of-care/availability?${params}`,
        {
          method: "GET",
          headers: debug && token ? { Authorization: `Bearer ${token}` } : undefined
        }
      );
      if (response.status !== 200) {
        if (
          response.status === 404 ||
          response.status === 400 ||
          response.status === 410
        ) {
          throw new Error((await response.json()).message);
        } else {
          throw new Error("Error loading appointment availability");
        }
      }
      const data = await response.json();
      if (debug) {
        // eslint-disable-next-line no-console
        console.log("[debug availability] plan-of-care response", data);
      }
      return data;
    },
    retry: false,
    staleTime: 1000 * 60 * 5, // cache for 5 minutes
    enabled
  });
}
